import { apiClient } from '@/core/api/client';

/**
 * Fetch the care-seeker's step-5 care plan PDF as an authenticated blob, then
 * trigger a browser download via an in-memory object URL. Blob fetch (rather
 * than a plain anchor href) is required because the FE talks to the backend
 * cross-origin, so the HTML `download` attribute would be ignored.
 *
 * Returns the suggested filename the backend sent back (from the
 * Content-Disposition header) so callers can surface it in a toast / aria.
 */
export async function downloadCarePlanPdf(
  sessionId: string,
  fallbackFilename = 'careplan.pdf',
): Promise<string> {
  const res = await apiClient.get<Blob>('/care-plan-pdf', {
    params: { session_id: sessionId },
    responseType: 'blob',
  });
  const blob =
    res.data instanceof Blob
      ? res.data
      : new Blob([res.data as ArrayBuffer], { type: 'application/pdf' });

  const disposition =
    (res.headers as Record<string, string | undefined>)['content-disposition'] ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);

  return filename;
}
